import { createContext, useContext, useEffect, useState, useCallback } from "react";
import { api, imgUrl } from "../lib/api";
import { dict } from "../i18n/dict";

// Contenido por defecto: la página se pinta al instante y se actualiza
// cuando la API responde. Evita pantallas negras en conexiones lentas.
const FALLBACK_SITE = {
  brand: { logo_url: "/api/files/seed-logo", logo_light_url: "", favicon_url: "/api/files/seed-logo", header_logo_height: 48 },
  hero: {
    slogan_es: "TECNOLOGÍA EN GRAN ESCALA GLOBAL",
    slogan_en: "GLOBAL SCALE TECHNOLOGY",
    title_es: "TU IDEA PUEDE CONVERTIRSE EN UNA SOLUCIÓN TECNOLÓGICA.",
    title_en: "YOUR IDEA CAN BECOME A TECHNOLOGY SOLUTION.",
    subtitle_es: "Creamos aplicaciones, tiendas en línea, plataformas web y sistemas personalizados para ayudar a los negocios a trabajar mejor, ahorrar tiempo y crecer.",
    subtitle_en: "We build apps, online stores, web platforms, and custom business systems to help businesses work smarter, save time, and grow.",
    description_es: "Del concepto al lanzamiento, construimos contigo en cada etapa.",
    description_en: "From concept to launch, we build with you at every step.",
    btn_primary_es: "Explorar soluciones", btn_primary_en: "Explore Solutions",
    btn_secondary_es: "Solicitar una cotización", btn_secondary_en: "Request a Quote",
    banner_desktop: "/api/files/seed-banner-desktop",
    banner_mobile: "/api/files/seed-banner-mobile",
    image_position: "center", content_align: "left",
  },
  sections: {
    projects_eyebrow_es: "PORTAFOLIO", projects_eyebrow_en: "PORTFOLIO",
    projects_title_es: "Soluciones que construimos", projects_title_en: "Solutions we build",
    services_eyebrow_es: "SERVICIOS", services_eyebrow_en: "SERVICES",
    services_title_es: "Servicios y paquetes", services_title_en: "Services & Packages",
    process_eyebrow_es: "PROCESO", process_eyebrow_en: "PROCESS",
    process_title_es: "De la idea al lanzamiento", process_title_en: "From idea to launch",
    contact_eyebrow_es: "CONTACTO", contact_eyebrow_en: "CONTACT",
    contact_title_es: "Cuéntanos tu proyecto", contact_title_en: "Tell us about your project",
    footer_text_es: "Desarrollo de aplicaciones, tiendas en línea y sistemas empresariales a escala global.",
    footer_text_en: "Apps, online stores and business systems built at global scale.",
  },
  contact: { email: "", whatsapp: "", business_info_es: "", business_info_en: "" },
  social: {},
  settings: { public_url: "", default_lang: "en" },
};

const SiteCtx = createContext(null);

export function SiteProvider({ children }) {
  const [site, setSite] = useState(FALLBACK_SITE);
  const [lang, setLangState] = useState(() => localStorage.getItem("teravix-lang") || "");

  useEffect(() => {
    api.get("/site").then((r) => {
      setSite(r.data);
      setLangState((prev) => prev || r.data?.settings?.default_lang || "en");
    }).catch(() => {});
  }, []);

  useEffect(() => {
    if (site?.brand?.favicon_url) {
      let link = document.querySelector("link[rel='icon']");
      if (!link) {
        link = document.createElement("link");
        link.rel = "icon";
        document.head.appendChild(link);
      }
      link.href = imgUrl(site.brand.favicon_url);
    }
  }, [site]);

  useEffect(() => {
    if (lang) document.documentElement.lang = lang;
  }, [lang]);

  const setLang = useCallback((l) => {
    localStorage.setItem("teravix-lang", l);
    setLangState(l);
  }, []);

  const t = useCallback((path) => {
    const d = dict[lang || "en"] || dict.en;
    return path.split(".").reduce((o, k) => (o ? o[k] : undefined), d);
  }, [lang]);

  const L = useCallback((obj, base) => {
    if (!obj) return "";
    return obj[`${base}_${lang || "en"}`] ?? obj[`${base}_en`] ?? obj[`${base}_es`] ?? "";
  }, [lang]);

  return (
    <SiteCtx.Provider value={{ site, lang: lang || "en", setLang, t, L }}>
      {children}
    </SiteCtx.Provider>
  );
}

export const useSite = () => useContext(SiteCtx);
